import { EAttributeType, IAttributeLocalization } from '../@types/AttributeTypes';
import { EMaterialMapType, I3dModel, IFilesApiColor, IFilesApiObject, IMaterial, IMaterialMap } from '../@types/FilesAPIModels';
import { getValidFloorplannerKeycloakAccessToken } from '../keycloak';
import AttributeModel, { IAttribute } from '../models/standard/AttributeModel';
import FilesAPIProductModel from '../models/standard/FilesAPIProductModel';
import { FILES_API_URL } from './constants';
import { parseFilesApiAttributesRequest } from './jsonParser';

const apiUrl = FILES_API_URL;
export const FILES_API_ATTRIBUTE_CREATED_EVENT = 'filesapi:attribute-created';
const FILES_API_KEYCLOAK_COOKIE_PATH = '/imsFilesApi';
const FILES_API_DEMO_COOKIE_NAMES = ['accessToken', 'refreshToken'];

type FilesApiAuthMode = 'demo' | 'keycloak';

interface FilesApiAuthentication {
  mode: FilesApiAuthMode;
  accessToken?: string;
}

interface FilesApiCredentials {
  username: string;
  password: string;
}

interface FilesApiLoginResponse {
  accessToken?: string;
  status?: string;
}

interface CreateFilesApiAttributeData {
  localization: IAttributeLocalization;
  publicFlag?: boolean;
  type?: EAttributeType;
}

let demoLoginPromise: Promise<boolean> | undefined;

const getDemoCredentials = (): FilesApiCredentials => ({
  username: process.env.REACT_APP_USERNAME || 'user',
  password: process.env.REACT_APP_PASSWORD || 'user',
});

const clearCookieByNameAndPath = (name: string, path: string): void => {
  document.cookie = [`${name}=`, 'Max-Age=0', `path=${path}`, 'Secure', 'SameSite=None'].join('; ');
  document.cookie = [`${name}=`, 'Max-Age=0', `path=${path}`].join('; ');
};

const clearFilesApiDemoCookies = (): void => {
  if (typeof document === 'undefined') return;

  FILES_API_DEMO_COOKIE_NAMES.forEach((name) => {
    clearCookieByNameAndPath(name, FILES_API_KEYCLOAK_COOKIE_PATH);
  });
};

const requestFilesApiLogin = async (data: FilesApiCredentials): Promise<FilesApiLoginResponse | string> => {
  try {
    const response = await fetch(`${apiUrl}/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
      credentials: 'include',
    });
    return response.json();
  } catch (error) {
    // console.log('An API error occurred while performing a login request! Status: ', JSON.stringify(error, null, 2));
    return 'An API error occurred while performing a login request! Status: ' + JSON.stringify(error, null, 2);
  }
};

const isFilesApiLoginResponse = (response: FilesApiLoginResponse | string): response is FilesApiLoginResponse => typeof response === 'object' && response !== null;

const isFilesApiLoginSuccessful = (response: FilesApiLoginResponse | string): boolean => {
  if (!isFilesApiLoginResponse(response)) return false;

  return !!response.accessToken || response.status === 'ACCESS GRANTED';
};

const ensureDemoFilesApiLogin = async (): Promise<boolean> => {
  if (!demoLoginPromise) {
    demoLoginPromise = requestFilesApiLogin(getDemoCredentials())
      .then(isFilesApiLoginSuccessful)
      .finally(() => {
        demoLoginPromise = undefined;
      });
  }

  return demoLoginPromise;
};

const buildBearerHeaders = (headers: HeadersInit | undefined, accessToken: string): Headers => {
  const requestHeaders = new Headers(headers);
  requestHeaders.set('Authorization', `Bearer ${accessToken}`);
  return requestHeaders;
};

const ensureFilesApiAuthentication = async (): Promise<FilesApiAuthentication> => {
  const keycloakAccessToken = await getValidFloorplannerKeycloakAccessToken();

  if (keycloakAccessToken) {
    clearFilesApiDemoCookies();
    return { mode: 'keycloak', accessToken: keycloakAccessToken };
  }

  await ensureDemoFilesApiLogin();
  return { mode: 'demo' };
};

const getFilesApiKeycloakAccessToken = async (): Promise<string | undefined> => {
  const keycloakAccessToken = await getValidFloorplannerKeycloakAccessToken();

  if (!keycloakAccessToken) {
    return undefined;
  }

  clearFilesApiDemoCookies();
  return keycloakAccessToken;
};

const logoutFilesApi = async (): Promise<void> => {
  try {
    await fetch(`${apiUrl}/logout`, {
      method: 'POST',
      credentials: 'include',
    });
  } catch (error) {
    console.error('Failed to logout from FilesAPI:', error);
  } finally {
    clearFilesApiDemoCookies();
  }
};

const buildFilesApiRequestOptions = async (options: RequestInit = {}): Promise<RequestInit> => {
  const authentication = await ensureFilesApiAuthentication();

  if (authentication.mode === 'keycloak' && authentication.accessToken) {
    return {
      ...options,
      headers: buildBearerHeaders(options.headers, authentication.accessToken),
      credentials: 'omit',
    };
  }

  return {
    ...options,
    credentials: 'include',
  };
};

const parseFilesApiAttribute = (item: any): IAttribute => ({
  id: item.id,
  key: item.key,
  type: item.type,
  unit: item.unit || null,
  predecessorId: item.predecessorId || null,
  hidden: item.hidden || false,
  forced: item.forced || false,
  value: item.value,
  unitFamily: item.unitFamily,
  ownerId: item.ownerId,
  userId: item.userId,
  publicFlag: item.publicFlag,
  localization: item.localization,
  defaults: item.defaults,
  validation: item.validation,
});

const notifyAttributeCreated = (attribute: IAttribute): void => {
  if (typeof window === 'undefined') return;

  window.dispatchEvent(new CustomEvent<IAttribute>(FILES_API_ATTRIBUTE_CREATED_EVENT, { detail: attribute }));
};

// TODO: Better error handling needed!

/**
 * Files API Service component that is used to communicate with the imsFilesAPI.
 */
const filesApiService = {
  ensureAuthenticated: ensureFilesApiAuthentication,

  logout: logoutFilesApi,

  createAttribute: async (data: CreateFilesApiAttributeData): Promise<IAttribute> => {
    const keycloakAccessToken = await getFilesApiKeycloakAccessToken();

    if (!keycloakAccessToken) {
      throw new Error('Keycloak authentication is required to create attributes.');
    }

    const response = await fetch(`${apiUrl}/attribute`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        Authorization: `Bearer ${keycloakAccessToken}`,
      },
      body: JSON.stringify({
        type: data.type || EAttributeType.TEXT,
        localization: data.localization,
        publicFlag: data.publicFlag ?? false,
      }),
      credentials: 'omit',
    });

    if (!response.ok) {
      throw new Error(`Failed to create attribute. Status: ${response.status}`);
    }

    const createdAttribute = parseFilesApiAttribute(await response.json());
    notifyAttributeCreated(createdAttribute);
    return createdAttribute;
  },

  /**
   * Function that authenticates the login of a given user.
   *
   * @param {{username: string; password: string}} data object containing the username and password to authenticate further calls.
   * @returns json object containing the accessToken string that is needed for all additional calls.
   */
  loginRequest: async (data: FilesApiCredentials) => requestFilesApiLogin(data),

  /**
   * Function to request data from a specific endpoint in our API.
   * @param {string} endpoint the api endpoint that will get attached to the apiUrl.
   * @returns data object containing the response of the server.
   */
  dataRequest: async (endpoint: string) => {
    try {
      const response = await fetch(`${apiUrl}/${endpoint}`, {
        ...(await buildFilesApiRequestOptions({
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        })),
      });
      return response.json();
    } catch (error) {
      // console.log('An API error occurred while performing a data request! Status: ', JSON.stringify(error, null, 2));
      return 'An API error occurred while performing a data request! Status: ' + JSON.stringify(error, null, 2);
    }
  },

  /**
   * Function to request data from a specific endpoint in our API.
   * Returns true if the request is successful (not 5xx or timeout), otherwise false.
   * @returns {Promise<boolean>} True if request was successful, otherwise false.
   */
  checkFilesApiStatus: async (): Promise<boolean> => {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 5000); // 5-second timeout

    try {
      const response = await fetch(`${apiUrl}/model/1`, {
        ...(await buildFilesApiRequestOptions({
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
          signal: controller.signal,
        })),
      });

      clearTimeout(timeout);

      // HTTP status code 5xx
      if (response.status >= 500 && response.status < 600) {
        return false;
      }

      return true;
    } catch (error: any) {
      clearTimeout(timeout);

      if (error.name === 'AbortError') {
        // console.log('Request timed out');
      } else {
        // console.log('An API error occurred:', error);
      }
      return false;
    }
  },

  dataWithParamsRequest: async (path: string, params: Record<string, string> = {}) => {
    try {
      const query = new URLSearchParams(params).toString();
      const url = query ? `${apiUrl}/${path}?${query}` : `${apiUrl}/${path}`;

      const response = await fetch(url, {
        ...(await buildFilesApiRequestOptions({
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
        })),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('An API error occurred while performing a data request! Status: ', errorText);
        return;
      }

      const data = await response.json();
      // Extract the 'content' property from the response
      if (data && Array.isArray(data.content)) {
        return data.content; // Return the array directly
      }
      console.error('An API error occurred while performing a data request!');
    } catch (error) {
      console.error('An API error occurred while performing a data request! Status: ', error);
      throw error; // Rethrow the error so the caller can handle it
    }
  },
  /**
   * Function to generically request an image from the imsFilesApi.
   *
   * @param {string} endpoint the api endpoint that will get attached to the apiUrl.
   * @returns {string} URL that refers to the fetched image.
   */
  imageRequest: async (endpoint: string): Promise<string> => {
    try {
      const response = await fetch(`${apiUrl}/${endpoint}`, {
        ...(await buildFilesApiRequestOptions({
          method: 'GET',
          headers: {
            'Content-Type': 'image/png',
          },
        })),
      });
      if (!response.ok) {
        throw new Error(`An HTTP error occurred while performing an image request! Status: "${response.status}"`);
      }
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      return url;
    } catch (error) {
      // console.log('An API error occurred! Status: ', JSON.stringify(error, null, 2));
      return 'An API error occurred! Status: ' + JSON.stringify(error, null, 2);
    }
  },
  /**
   * Function to request the preview picture of a given product in a specified quality.
   *
   * @param {number} pId numerical identifier of the product.
   * @param {string} quality refers to the resolution of the requested image.
   * @returns {Promise<string>} URL that refers to the fetched preview picture.
   */
  previewImageRequest: async (pId: number, quality: string = 'thumb'): Promise<string> => {
    try {
      const response = await fetch(`${apiUrl}/product/${pId}/previewPicture?quality=${quality}`, {
        ...(await buildFilesApiRequestOptions({
          method: 'GET',
          headers: { 'Content-Type': 'image/png' },
        })),
      });
      if (!response.ok) {
        if (response.status === 404) {
          // Preview image not found—return an empty string without logging an error.
          return '';
        }
        throw new Error(`An HTTP error occurred while performing an image request! Status: "${response.status}"`);
      }
      const blob = await response.blob();
      return URL.createObjectURL(blob);
    } catch (error) {
      // Only log errors if they aren’t a simple 404.
      // console.log('An API error occurred while requesting a preview picture! Status: ', JSON.stringify(error, null, 2));
      return '';
    }
  },
  // eslint-disable-next-line complexity
  productPreviewImageRequest: async (product: FilesAPIProductModel, quality: string = 'thumb'): Promise<string> => {
    try {
      // Try the main preview request first.
      const response = await fetch(`${apiUrl}/product/${product.id}/previewPicture`, {
        ...(await buildFilesApiRequestOptions({
          method: 'GET',
          headers: { 'Content-Type': 'image/jpeg' },
        })),
      });
      if (!response.ok) {
        if (response.status === 404) {
          // If 404, try alternatives below.
        } else {
          throw new Error(`HTTP error while requesting product preview picture. Status: "${response.status}"`);
        }
      } else {
        const blob = await response.blob();
        return URL.createObjectURL(blob);
      }
      // If no preview picture is found, try material assets:
      if (product.materialAssets.length !== 0) {
        for (let i = 0; i < product.materialAssets.length; i++) {
          if (product.materialAssets[i].maps.length !== 0) {
            for (let o = 0; o < product.materialAssets[i].maps.length; o++) {
              if (product.materialAssets[i].maps[o].type === EMaterialMapType.map) {
                const responseMaterialPreview = await fetch(`${apiUrl}/material/${product.materialAssets[i].id}/map/${product.materialAssets[i].maps[o].id}?quality=${quality}`, {
                  ...(await buildFilesApiRequestOptions({
                    method: 'GET',
                    headers: { 'Content-Type': 'image/jpeg' },
                  })),
                });
                if (!responseMaterialPreview.ok) {
                  if (responseMaterialPreview.status === 404) {
                    continue; // skip this one
                  }
                  throw new Error(`HTTP error for material preview. Status: "${responseMaterialPreview.status}"`);
                }
                const blob = await responseMaterialPreview.blob();
                return URL.createObjectURL(blob);
              }
            }
          }
        }
      } else if (product.modelAssets.length !== 0) {
        for (let i = 0; i < product.modelAssets.length; i++) {
          if (product.modelAssets[i].previewPicture !== undefined && product.modelAssets[i].previewPicture !== null) {
            const responseModelPreview = await fetch(`${apiUrl}/product/${product.id}/model/${product.modelAssets[0].id}/previewPicture?quality=${quality}`, {
              ...(await buildFilesApiRequestOptions({
                method: 'GET',
                headers: { 'Content-Type': 'image/jpeg' },
              })),
            });
            if (!responseModelPreview.ok) {
              if (responseModelPreview.status === 404) {
                continue;
              }
              throw new Error(`HTTP error for model preview. Status: "${responseModelPreview.status}"`);
            }
            const blob = await responseModelPreview.blob();
            return URL.createObjectURL(blob);
          } else if (product.modelAssets[i].file2d !== undefined && product.modelAssets[i].file2d !== null) {
            const response2d = await fetch(`${apiUrl}/product/${product.id}/model/${product.modelAssets[i].id}/file?type=2d`, {
              ...(await buildFilesApiRequestOptions({
                method: 'GET',
                headers: { 'Content-Type': 'image/svg+xml' },
              })),
            });
            if (!response2d.ok) {
              if (response2d.status === 404) {
                continue;
              }
              throw new Error(`HTTP error for 2d file. Status: "${response2d.status}"`);
            }
            const blob = await response2d.blob();
            return URL.createObjectURL(blob);
          }
        }
      }
      return ''; // If no preview image is found.
    } catch (error) {
      // console.log('An API error occurred while requesting a preview picture! Status: ', JSON.stringify(error, null, 2));
      return '';
    }
  },
  materialPreviewPictureRequest: async (materialId: number, quality: string = 'thumb'): Promise<string> => {
    try {
      const response = await fetch(`${apiUrl}/material/${materialId}/previewPicture?quality=${quality}`, {
        ...(await buildFilesApiRequestOptions({
          method: 'GET',
          headers: { 'Content-Type': 'image/png' },
        })),
      });
      if (!response.ok) {
        if (response.status === 404) {
          return '';
        }
        console.error(`An HTTP error occurred while requesting a material preview picture! Status: "${response.status}"`);
        return '';
      }
      const blob = await response.blob();
      return URL.createObjectURL(blob);
    } catch (error) {
      // console.log('An API error occurred while requesting a material preview picture! Status: ', JSON.stringify(error, null, 2));
      return '';
    }
  },
  modelPreviewImageRequest: async (model: I3dModel, quality: string = 'thumb'): Promise<string> => {
    if (!model.previewPicture) return '';
    try {
      const response = await fetch(`${apiUrl}/model/${model.id}/previewPicture?quality=${quality}`, {
        ...(await buildFilesApiRequestOptions({
          method: 'GET',
          headers: { 'Content-Type': 'image/jpeg' },
        })),
      });

      if (response.ok) {
        const blob = await response.blob();
        return URL.createObjectURL(blob);
      } else if (response.status === 404) {
        // No preview available
        return '';
      }
      throw new Error(`HTTP error while requesting model preview picture. Status: "${response.status}"`);
    } catch (error) {
      console.error('API error while fetching model preview picture:', error);
      return '';
    }
  },
  getProductMaterial: async (productId: number, materialId: number): Promise<IMaterial | undefined> => {
    try {
      const material = await filesApiService.dataRequest(`product/${productId}/material/${materialId}`);
      return material as IMaterial;
    } catch (error) {
      console.error(`Error fetching material ${materialId} for product ${productId}:`, error);
      return undefined;
    }
  },
  /**
   * Fetches a color asset that belongs to a specific product.
   * @param productId - The product’s ID.
   * @param colorId - The color asset’s ID.
   * @returns Promise<IFilesApiColor|undefined>
   */
  getProductColorById: async (productId: number, colorId: number): Promise<IFilesApiColor | undefined> => {
    try {
      const response = await fetch(`${apiUrl}/product/${productId}/color/${colorId}`, {
        ...(await buildFilesApiRequestOptions({
          method: 'GET',
        })),
      });
      if (!response.ok) {
        throw new Error(`Failed to fetch product color. Status: ${response.status}`);
      }
      return (await response.json()) as IFilesApiColor;
    } catch (error) {
      console.error(`Error fetching color ${colorId} for product ${productId}:`, error);
      return undefined;
    }
  },

  /**
   * Fetches the material by its ID.
   * @param {number} materialId - The ID of the material to fetch.
   * @returns {Promise<IMaterial | undefined>} - A promise that resolves to the IMaterial object or undefined on error.
   */
  getMaterial: async (materialId: number): Promise<IMaterial | undefined> => {
    try {
      const response = await fetch(`${apiUrl}/material/${materialId}`, {
        ...(await buildFilesApiRequestOptions({
          method: 'GET',
        })),
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch material. Status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error(`Error fetching material with ID ${materialId}:`, error);
      return undefined;
    }
  },

  /**
   * Fetches the 'map' image for a material asset that belongs to a specific product.
   * @param productId - The ID of the product.
   * @param materialId - The ID of the material asset.
   * @param mapId - The specific map's ID within that material.
   * @returns Promise<string> URL or empty string.
   */
  getProductMaterialMapImage: async (productId: number, materialId: number, mapId: number): Promise<string> => {
    try {
      const response = await fetch(`${apiUrl}/product/${productId}/material/${materialId}/map/${mapId}`, {
        ...(await buildFilesApiRequestOptions({
          method: 'GET',
          headers: { 'Content-Type': 'image/jpeg' },
        })),
      });
      if (!response.ok) {
        if (response.status === 404) {
          return '';
        }
        throw new Error(`Failed to fetch product material map. Status: ${response.status}`);
      }
      const blob = await response.blob();
      return URL.createObjectURL(blob);
    } catch (err) {
      console.error(`Error fetching product material map for ${materialId} of product ${productId}:`, err);
      return '';
    }
  },

  /**
   * Fetches the image URL for the map of type 'EMaterialMapType.map' from the material.
   * @param {number} materialId - The ID of the material.
   * @param {IMaterial} material - The fetched material object.
   * @returns {Promise<string | undefined>} - A promise that resolves to the image URL or undefined on error.
   */
  getMaterialMapImage: async (materialId: number, material: IMaterial): Promise<string | undefined> => {
    try {
      // Find the map of type 'EMaterialMapType.map'
      const map = material.maps.find((m: IMaterialMap) => m.type === EMaterialMapType.map);

      if (!map) {
        console.warn(`No map of type '${EMaterialMapType.map}' found for material ID ${materialId}`);
        return undefined;
      }

      const response = await fetch(`${apiUrl}/material/${materialId}/map/${map.id}`, {
        ...(await buildFilesApiRequestOptions({
          method: 'GET',
        })),
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch map image. Status: ${response.status}`);
      }

      const blob = await response.blob();
      return URL.createObjectURL(blob);
    } catch (error) {
      console.error(`Error fetching material image for ID ${materialId}:`, error);
      return undefined;
    }
  },

  /**
   * Fetches the SVG file for a given model ID.
   *
   * @param {number} modelId - The ID of the model to fetch the SVG file for.
   * @returns {Promise<string>} - A promise that resolves to a URL for the fetched SVG file.
   */
  getSVGFileFromModel: async (modelId: number): Promise<string> => {
    try {
      const response = await fetch(`${apiUrl}/model/${modelId}/file?type=2d`, {
        ...(await buildFilesApiRequestOptions({
          method: 'GET',
          headers: {
            'Content-Type': 'image/svg+xml',
          },
        })),
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch SVG file. Status: ${response.status}`);
      }

      const blob = await response.blob();
      return URL.createObjectURL(blob);
    } catch (error) {
      console.error(`Error fetching SVG file for model ID ${modelId}:`, error);
      throw error;
    }
  },

  /**
   * Fetches the SVG file for a given product and model ID.
   *
   * @param {number} productId - The ID of the product.
   * @param {number} modelId - The ID of the model to fetch the SVG file for.
   * @returns {Promise<string>} - A promise that resolves to a URL for the fetched SVG file.
   */
  getSVGFileFromProduct: async (productId: number, modelId: number): Promise<string> => {
    try {
      const response = await fetch(`${apiUrl}/product/${productId}/model/${modelId}/file?type=2d`, {
        ...(await buildFilesApiRequestOptions({
          method: 'GET',
          headers: {
            'Content-Type': 'image/svg+xml',
          },
        })),
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch SVG file. Status: ${response.status}`);
      }

      const blob = await response.blob();
      return URL.createObjectURL(blob);
    } catch (error) {
      console.error(`Error fetching SVG file for product ID ${productId} and model ID ${modelId}:`, error);
      throw error;
    }
  },

  /**
   * Fetches a 3D model by its product ID and model ID.
   *
   * @param {number} productId - The ID of the product.
   * @param {number} modelId - The ID of the 3D model to fetch.
   * @returns {Promise<I3dModel | undefined>} - The model object or undefined on error.
   */
  getProductModelById: async (productId: number, modelId: number): Promise<I3dModel | undefined> => {
    try {
      const response = await fetch(`${apiUrl}/product/${productId}/model/${modelId}`, {
        ...(await buildFilesApiRequestOptions({
          method: 'GET',
        })),
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch model. Status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error(`Error fetching model with product ID ${productId} and model ID ${modelId}:`, error);
      return undefined;
    }
  },
  /**
   * Fetches a 3D model by its ID.
   *
   * @param {number} modelId - The ID of the 3D model to fetch.
   * @returns {Promise<I3dModel | undefined>} - The model object or undefined on error.
   */
  getModelById: async (modelId: number): Promise<I3dModel | undefined> => {
    try {
      const response = await fetch(`${apiUrl}/model/${modelId}`, {
        ...(await buildFilesApiRequestOptions({
          method: 'GET',
        })),
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch model. Status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error(`Error fetching model with ID ${modelId}:`, error);
      return undefined;
    }
  },
  /**
   * Fetches a color by its ID.
   *
   * @param {number} colorId - The ID of the 3D model to fetch.
   * @returns {Promise<IFilesApiColor | undefined>} - The color or undefined on error.
   */
  getColorById: async (colorId: number): Promise<IFilesApiColor | undefined> => {
    try {
      const response = await fetch(`${apiUrl}/color/${colorId}`, {
        ...(await buildFilesApiRequestOptions({
          method: 'GET',
        })),
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch model. Status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error(`Error fetching model with ID ${colorId}:`, error);
      return undefined;
    }
  },

  /**
   * Fetches all objects from the API.
   * @returns {Promise<IFilesApiObject[]>} - Array of objects.
   */
  getObjects: async (region?: string): Promise<IFilesApiObject[]> => {
    try {
      await ensureFilesApiAuthentication();
      // Append country param if provided
      let query = 'object/all?page=0&size=10000&published=true&all=true&addInfo=true';
      if (region && region !== '') {
        query += `&country=${region}`;
      }

      const response = await filesApiService.dataRequest(query);
      return response || [];
    } catch (error) {
      console.error('Error fetching objects:', error);
      throw new Error(`Error fetching objects: ${error}`);
    }
  },

  /**
   * Fetches all published attributes from the API.
   * @returns {Promise<IAttribute[]>} - Array of attributes.
   */
  getAttributes: async (region?: string): Promise<IAttribute[]> => {
    try {
      await ensureFilesApiAuthentication();
      let query = 'attribute/all?page=0&size=10000&published=true&all=true&usage=true&addInfo=true';
      if (region && region !== '') {
        query += `&country=${region}`;
      }

      const response = await filesApiService.dataRequest(query);
      return parseFilesApiAttributesRequest(response);
    } catch (error) {
      console.error('Error fetching attributes:', error);
      throw new Error(`Error fetching attributes: ${error}`);
    }
  },

  /**
   * Fetches an object by its ID, including its attributes.
   * @param {number} id - The ID of the object to fetch.
   * @param {string} [region] - The country code (e.g. "US", "DE") to fetch region-specific defaults.
   * @returns {Promise<IFilesApiObject | null>} - The object with parsed attributes or null on error.
   */
  getObjectById: async (id: number, region?: string): Promise<IFilesApiObject | null> => {
    try {
      await ensureFilesApiAuthentication();

      // Construct query with region
      let query = `object/${id}?addInfo=true`;
      if (region && region !== '') {
        query += `&country=${region}`;
      }

      const response = await filesApiService.dataRequest(query);

      // Parse wrapper attributes
      const attrs: AttributeModel[] =
        response.attributes?.map((wrapper: any, i: number) => {
          const def = wrapper.attribute || wrapper;
          const pred = i === 0 ? null : response.attributes[i - 1].attribute?.id || response.attributes[i - 1].id;

          const value = wrapper.value !== undefined ? wrapper.value : def.value;

          let extractedUnit = wrapper.unit !== undefined ? wrapper.unit : def.unit;

          if (def.type === EAttributeType.NUMBER) {
            const activeRegion = region ? region.toUpperCase() : 'DE';
            const validationObj = def.validation?.[activeRegion] || def.validation?.GLOBAL || {};
            const defaultsObj = def.defaults?.[activeRegion] || def.defaults?.GLOBAL || {};

            // Prefer unit from wrapper -> validation -> defaults -> def root
            extractedUnit = wrapper.unit !== undefined ? wrapper.unit : validationObj.unit || defaultsObj.unit || def.unit;
          }

          return new AttributeModel(
            Number(def.id),
            def.key || '',
            value,
            pred,
            def.type as EAttributeType,
            extractedUnit,
            wrapper.hidden ?? def.hidden ?? false,
            wrapper.forced ?? def.forced ?? false,
            def.ownerId,
            def.userId,
            def.publicFlag,
            def.localization,
            def.defaults,
            def.validation,
            def.unitFamily,
          );
        }) || [];

      return { ...response, attributes: attrs };
    } catch (error) {
      console.error(`Error fetching object with ID ${id}:`, error);
      throw new Error(`Error fetching object with ID ${id}: ${error}`);
    }
  },
};

export default filesApiService;

// Utility function to format a product model as a JSON representation of itself to post it to the database.
const formatProductForPostRequest = (p: FilesAPIProductModel): string => {
  const formattedProductString = `{"id":${p.id},"description":"${p.description}","link":"${p.links[0]}","manufacturer_id":${p.manufacturer.id},"product_id":"${p.productId}","name":"${p.name}","description":"${p.description}","category":{"id":${p.objects} },"preview_picture_id":${p.previewPicture.id},"public_flag":"${p.publicFlag}"}`;
  return formattedProductString;
};
